import React, { useRef } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const QRCodeModal = ({
  trail,
  onClose,
  qrSize,
  onSizeChange,
  errorCorrection,
  onErrorCorrectionChange,
  printSettings,
  onPrintSettingsChange,
  onPrint,
  onDownload
}) => {
  const qrCodeRef = useRef(null);

  const sizeOptions = [
    { value: 'small', label: 'Small (200×200px)' },
    { value: 'medium', label: 'Medium (300×300px)' },
    { value: 'large', label: 'Large (400×400px)' },
    { value: 'xlarge', label: 'Extra Large (500×500px)' }
  ];

  const errorCorrectionOptions = [
    { value: 'L', label: 'Low (7% recovery)' },
    { value: 'M', label: 'Medium (15% recovery)' },
    { value: 'Q', label: 'Quartile (25% recovery)' },
    { value: 'H', label: 'High (30% recovery)' }
  ];

  const paperSizeOptions = [
    { value: 'letter', label: 'Letter (8.5" × 11")' },
    { value: 'a4', label: 'A4 (210mm × 297mm)' },
    { value: 'legal', label: 'Legal (8.5" × 14")' },
    { value: 'tabloid', label: 'Tabloid (11" × 17")' }
  ];

  const orientationOptions = [
    { value: 'portrait', label: 'Portrait' },
    { value: 'landscape', label: 'Landscape' }
  ];

  const layoutOptions = [
    { value: 'single', label: 'Single Code (1 per page)' },
    { value: 'double', label: 'Double (2 per page)' },
    { value: 'quad', label: 'Quad (4 per page)' },
    { value: 'grid', label: 'Grid (6 per page)' },
    { value: 'sheet', label: 'Sheet (9 per page)' }
  ];

  const getSizeInPixels = () => {
    const sizes = { small: 200, medium: 300, large: 400, xlarge: 500 };
    return sizes?.[qrSize] || 300;
  };

  const getTrailUrl = () => {
    return `${window.location?.origin}/qr-code-scanner?trailId=${trail?.id}`;
  };

  const handleSettingChange = (field, value) => {
    onPrintSettingsChange({ ...printSettings, [field]: value });
  };

  return (
    <div className="fixed inset-0 z-[1001] flex items-center justify-center px-4">
      {/* Backdrop - fixed, always covers full viewport */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal - fixed height with internal scroll */}
      <div className="relative bg-card rounded-xl shadow-2xl w-full max-w-4xl flex flex-col" style={{maxHeight: 'calc(100vh - 120px)', marginTop: '80px'}}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="QrCode" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground leading-tight">{trail?.title}</h2>
              <p className="text-xs text-muted-foreground">{trail?.classroom} · {trail?.subject}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-border">

            {/* Left: QR Code Preview */}
            <div className="p-6 space-y-5">
              <h3 className="text-base font-semibold text-foreground">QR Code Preview</h3>

              <div className="bg-muted/30 border border-dashed border-border rounded-lg p-6 flex items-center justify-center">
                <div ref={qrCodeRef} className="bg-white p-4 rounded-lg shadow-md">
                  <QRCodeCanvas
                    value={getTrailUrl()}
                    size={getSizeInPixels()}
                    level={errorCorrection}
                    bgColor="#ffffff"
                    fgColor="#2D3A2D"
                    includeMargin={false}
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Select
                  label="QR Code Size"
                  options={sizeOptions}
                  value={qrSize}
                  onChange={onSizeChange}
                />
                <Select
                  label="Error Correction Level"
                  description="Higher levels allow scanning even if code is partially damaged"
                  options={errorCorrectionOptions}
                  value={errorCorrection}
                  onChange={onErrorCorrectionChange}
                />
              </div>

              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground">
                    Students scan this QR code to access the learning trail directly. Print clearly for best results.
                  </p>
                </div>
              </div>
            </div>

            {/* Right: Print Settings */}
            <div className="p-6 space-y-5">
              <h3 className="text-base font-semibold text-foreground">Print Settings</h3>

              <div className="grid grid-cols-2 gap-3">
                <Select
                  label="Paper Size"
                  options={paperSizeOptions}
                  value={printSettings?.paperSize}
                  onChange={(v) => handleSettingChange('paperSize', v)}
                />
                <Select
                  label="Orientation"
                  options={orientationOptions}
                  value={printSettings?.orientation}
                  onChange={(v) => handleSettingChange('orientation', v)}
                />
              </div>

              <Select
                label="Layout"
                description="Number of QR codes per page"
                options={layoutOptions}
                value={printSettings?.layout}
                onChange={(v) => handleSettingChange('layout', v)}
              />

              <div>
                <p className="text-sm font-medium text-foreground mb-3">Margins (inches)</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Top"
                    type="number"
                    value={printSettings?.marginTop}
                    onChange={(e) => handleSettingChange('marginTop', e?.target?.value)}
                    placeholder="0.5"
                    min="0" max="2" step="0.1"
                  />
                  <Input
                    label="Bottom"
                    type="number"
                    value={printSettings?.marginBottom}
                    onChange={(e) => handleSettingChange('marginBottom', e?.target?.value)}
                    placeholder="0.5"
                    min="0" max="2" step="0.1"
                  />
                  <Input
                    label="Left"
                    type="number"
                    value={printSettings?.marginLeft}
                    onChange={(e) => handleSettingChange('marginLeft', e?.target?.value)}
                    placeholder="0.5"
                    min="0" max="2" step="0.1"
                  />
                  <Input
                    label="Right"
                    type="number"
                    value={printSettings?.marginRight}
                    onChange={(e) => handleSettingChange('marginRight', e?.target?.value)}
                    placeholder="0.5"
                    min="0" max="2" step="0.1"
                  />
                </div>
              </div>

              <div className="border-t border-border pt-4 space-y-3">
                <p className="text-sm font-medium text-foreground">Branding &amp; Options</p>
                <Checkbox
                  label="Include journey title"
                  description="Print title below QR code"
                  checked={printSettings?.includeTitle}
                  onChange={(e) => handleSettingChange('includeTitle', e?.target?.checked)}
                />
                <Checkbox
                  label="Include instructions"
                  description="Add scanning instructions for students"
                  checked={printSettings?.includeInstructions}
                  onChange={(e) => handleSettingChange('includeInstructions', e?.target?.checked)}
                />
                <Checkbox
                  label="Include school branding"
                  description="Add school logo and name"
                  checked={printSettings?.includeBranding}
                  onChange={(e) => handleSettingChange('includeBranding', e?.target?.checked)}
                />
                <Checkbox
                  label="Add border"
                  description="Decorative border around QR code"
                  checked={printSettings?.addBorder}
                  onChange={(e) => handleSettingChange('addBorder', e?.target?.checked)}
                />
                <Checkbox
                  label="Print in colour"
                  description="Use colour printing if available"
                  checked={printSettings?.printColor}
                  onChange={(e) => handleSettingChange('printColor', e?.target?.checked)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-border bg-muted/20 flex-shrink-0">
          <button
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Back to trail list
          </button>
          <div className="flex gap-3 w-full sm:w-auto">
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={onDownload}
              className="flex-1 sm:flex-none"
            >
              Download
            </Button>
            <Button
              variant="default"
              iconName="Printer"
              iconPosition="left"
              onClick={onPrint}
              className="flex-1 sm:flex-none"
            >
              Print QR Code
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeModal;
